import { toIsoDateLocal } from '../scheduleView/constants';
import i18n, { getDateLocale } from '../../i18n';

export const DATE_PRESET_OPTIONS = [
  { value: 'today', labelKey: 'datePresetToday' },
  { value: 'yesterday', labelKey: 'datePresetYesterday' },
  { value: 'thisWeek', labelKey: 'datePresetThisWeek' },
  { value: 'lastWeek', labelKey: 'datePresetLastWeek' },
  { value: 'thisMonth', labelKey: 'datePresetThisMonth' },
  { value: 'custom', labelKey: 'datePresetCustom' },
];

export const DEFAULT_DATE_PRESET = 'thisWeek';

export const PRETENDED_HOURS_OPTIONS = [
  { value: 7, hours: 7 },
  { value: 7.5, hours: 7.5 },
  { value: 8, hours: 8 },
  { value: 8.5, hours: 8.5 },
  { value: 9, hours: 9 },
  { value: 9.5, hours: 9.5 },
  { value: 10, hours: 10 },
];

export const WEEKDAY_CHIPS = [
  { index: 0, labelKey: 'weekdayMon' },
  { index: 1, labelKey: 'weekdayTue' },
  { index: 2, labelKey: 'weekdayWed' },
  { index: 3, labelKey: 'weekdayThu' },
  { index: 4, labelKey: 'weekdayFri' },
  { index: 5, labelKey: 'weekdaySat' },
  { index: 6, labelKey: 'weekdaySun' },
];

export const DEFAULT_WORKING_DAYS = '0,1,2,3,4';

export const STORAGE_KEYS = {
  datePreset: 'followus_teams_planning_date_preset',
  team: 'followus_teams_planning_team_id',
  member: 'followus_teams_planning_member_id',
  centre: 'followus_teams_planning_centre_id',
  pretendedHours: 'followus_pretended_hours_per_day',
  workingDays: 'followus_teams_planning_working_days',
  peopleFiltersExpanded: 'followus_teams_planning_people_filters_expanded',
};

export const SUMMARY_CARD_META = [
  {
    id: 'hours-not-opened',
    summaryKey: 'HNO',
    label: 'Hours Not Opened',
    hint: 'Unopened declared hours',
    tone: 'danger',
    icon: 'lock_clock',
  },
  {
    id: 'ho-hours',
    summaryKey: 'HO',
    label: 'HO Hours',
    hint: 'Total hours declared',
    tone: 'primary',
    icon: 'schedule',
  },
  {
    id: 'astreinte',
    summaryKey: 'Astreinte',
    label: 'Astreinte',
    hint: 'On-call hours declared',
    tone: 'amber',
    icon: 'notifications_active',
  },
  {
    id: 'formation',
    summaryKey: 'Formation',
    label: 'Formation',
    hint: 'Training hours declared',
    tone: 'yellow',
    icon: 'school',
  },
  {
    id: 'conges',
    summaryKey: 'Congés',
    label: 'Congés',
    hint: 'Leave hours declared',
    tone: 'emerald',
    icon: 'beach_access',
  },
  {
    id: 'absence',
    summaryKey: 'Absence',
    label: 'Absence',
    hint: 'Absence hours declared',
    tone: 'rose',
    icon: 'person_off',
  },
];

export const CATEGORY_STYLE = {
  HNO: { label: 'Hours Not Opened', color: '#ef4444', swatchClass: 'bg-red-500', barClass: 'bg-red-500' },
  HO: { label: 'HO Hours', color: '#124191', swatchClass: 'bg-primary', barClass: 'bg-primary' },
  Astreinte: { label: 'Astreinte', color: '#f59e0b', swatchClass: 'bg-amber-500', barClass: 'bg-amber-500' },
  Formation: { label: 'Formation', color: '#eab308', swatchClass: 'bg-yellow-500', barClass: 'bg-yellow-500' },
  Congés: { label: 'Congés', color: '#10b981', swatchClass: 'bg-emerald-500', barClass: 'bg-emerald-500' },
  Absence: { label: 'Absence', color: '#f43f5e', swatchClass: 'bg-rose-500', barClass: 'bg-rose-500' },
};

export const CATEGORY_ORDER = ['HNO', 'HO', 'Astreinte', 'Formation', 'Congés', 'Absence'];

const COLOR_TOKEN_HEX = {
  blue: '#124191',
  primary: '#ef4444',
  amber: '#f59e0b',
  green: '#10b981',
  yellow: '#eab308',
  rose: '#f43f5e',
};

const COLOR_TOKEN_SWATCH = {
  blue: 'bg-blue-500',
  primary: 'bg-primary',
  amber: 'bg-amber-500',
  green: 'bg-emerald-500',
  yellow: 'bg-yellow-500',
  rose: 'bg-rose-500',
};

const TONE_BY_COLOR = {
  blue: 'primary',
  primary: 'danger',
  amber: 'amber',
  green: 'emerald',
  yellow: 'yellow',
  rose: 'rose',
};

/** Build chart/order helpers from API categories metadata (stats.categories). */
export function resolveCategoryOrder(categoriesMeta) {
  if (Array.isArray(categoriesMeta) && categoriesMeta.length > 0) {
    return categoriesMeta.map((c) => c.code);
  }
  return CATEGORY_ORDER;
}

export function resolveCategoryStyle(categoriesMeta) {
  if (!Array.isArray(categoriesMeta) || categoriesMeta.length === 0) {
    return CATEGORY_STYLE;
  }
  const style = { ...CATEGORY_STYLE };
  categoriesMeta.forEach((c) => {
    const hex = String(c.color || '').startsWith('#')
      ? c.color
      : (COLOR_TOKEN_HEX[c.color] || COLOR_TOKEN_HEX.blue);
    style[c.code] = {
      label: c.label || c.code,
      color: hex,
      swatchClass: 'bg-slate-400',
      barClass: 'bg-slate-400',
    };
  });
  return style;
}

export function resolveSummaryCardMeta(categoriesMeta) {
  if (!Array.isArray(categoriesMeta) || categoriesMeta.length === 0) {
    return SUMMARY_CARD_META;
  }
  const knownByKey = Object.fromEntries(SUMMARY_CARD_META.map((c) => [c.summaryKey, c]));
  return categoriesMeta.map((c) => {
    const known = knownByKey[c.code];
    if (known) return known;
    return {
      id: `cat-${c.id || c.code}`,
      summaryKey: c.code,
      label: c.label || c.code,
      hint: 'Declared hours',
      tone: 'primary',
      icon: 'category',
    };
  });
}

/** Platform-blue scale for category charts (dark → light), aligned with CATEGORY_ORDER. */
export const CATEGORY_BLUE_SCALE = [
  '#0a2a5c',
  '#124191',
  '#1a56b8',
  '#3b78d1',
  '#6b9ae0',
  '#a8c5f5',
];

export function getCategoryBlueColor(categoryOrIndex) {
  const index =
    typeof categoryOrIndex === 'number'
      ? categoryOrIndex
      : CATEGORY_ORDER.indexOf(categoryOrIndex);
  if (index < 0) return CATEGORY_BLUE_SCALE[0];
  return CATEGORY_BLUE_SCALE[index % CATEGORY_BLUE_SCALE.length];
}

export function getDefaultDateRange() {
  return getDateRangeForPreset(DEFAULT_DATE_PRESET);
}

function startOfWeekMonday(date) {
  const base = new Date(date);
  base.setHours(0, 0, 0, 0);
  const day = base.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  base.setDate(base.getDate() + diff);
  return base;
}

function endOfWeekSunday(date) {
  const start = startOfWeekMonday(date);
  const end = new Date(start);
  end.setDate(start.getDate() + 6);
  return end;
}

export function getDateRangeForPreset(preset, baseDate = new Date()) {
  const today = new Date(baseDate);
  today.setHours(0, 0, 0, 0);

  switch (preset) {
    case 'today':
      return {
        startDate: toIsoDateLocal(today),
        endDate: toIsoDateLocal(today),
      };
    case 'yesterday': {
      const yesterday = new Date(today);
      yesterday.setDate(today.getDate() - 1);
      return {
        startDate: toIsoDateLocal(yesterday),
        endDate: toIsoDateLocal(yesterday),
      };
    }
    case 'lastWeek': {
      const end = startOfWeekMonday(today);
      end.setDate(end.getDate() - 1);
      const start = new Date(end);
      start.setDate(end.getDate() - 6);
      return {
        startDate: toIsoDateLocal(start),
        endDate: toIsoDateLocal(end),
      };
    }
    case 'thisMonth': {
      const start = new Date(today.getFullYear(), today.getMonth(), 1);
      const end = new Date(today.getFullYear(), today.getMonth() + 1, 0);
      return {
        startDate: toIsoDateLocal(start),
        endDate: toIsoDateLocal(end),
      };
    }
    case 'thisWeek':
    default: {
      const start = startOfWeekMonday(today);
      const end = endOfWeekSunday(today);
      return {
        startDate: toIsoDateLocal(start),
        endDate: toIsoDateLocal(end),
      };
    }
  }
}

export function formatLastReminder(value) {
  if (!value) return i18n.t('planning:never');
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return i18n.t('planning:never');
  return date.toLocaleString(getDateLocale(), {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function formatHours(value) {
  const num = Number(value);
  if (Number.isNaN(num)) return '0.0';
  return num.toFixed(1);
}

export function formatWorkloadLabel(iso) {
  if (!iso) return '';
  const date = new Date(`${iso}T00:00:00`);
  if (Number.isNaN(date.getTime())) return iso;
  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

export function readStoredValue(key, fallback) {
  try {
    const stored = localStorage.getItem(key);
    return stored == null || stored === '' ? fallback : stored;
  } catch {
    return fallback;
  }
}

export function writeStoredValue(key, value) {
  try {
    localStorage.setItem(key, String(value));
  } catch {
    // ignore
  }
}

export function toFilterOptions(items = [], allLabel) {
  return [
    { value: 'All', label: allLabel },
    ...items.map((item) => ({
      value: String(item.id),
      label: item.name,
    })),
  ];
}
